import * as React from "react";

import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagsEditor } from "@/shared/components/TagsEditor";
import { Device } from "@/shared/domain/devices";
import { useUpdateDevice } from "@/shared/domain/devices/hooks/useUpdateDevice";

interface UpdateDeviceTagsProps {
  device: Device;
  resourceOwnerId: string;
  readonly?: boolean;
  onTagsUpdated?: (device: Device) => void;
}

/**
 * A device's tags in the shared {@link TagsEditor}, each change saved as a
 * metadata changeset. Devices have no tag suggestions of their own, so it
 * offers the org's tags across record kinds.
 */
export const UpdateDeviceTags: React.FC<UpdateDeviceTagsProps> = ({
  device,
  resourceOwnerId,
  readonly = false,
  onTagsUpdated,
}) => {
  const { mutateAsync: updateDevice } = useUpdateDevice();

  const save = async (metadata_changeset: {
    put_tags?: string[];
    remove_tags?: string[];
  }) => {
    const record = await updateDevice({
      deviceId: device.device_id,
      resourceOwnerId,
      updates: { metadata_changeset },
    });
    onTagsUpdated?.(record);
  };

  return (
    <TagsEditor
      tags={device.tags}
      recordKey={device.device_id}
      readonly={readonly}
      autocompleteType={AutocompleteType.GenericTags}
      onAdd={(tag) => save({ put_tags: [tag] })}
      onRemove={(tag) => save({ remove_tags: [tag] })}
      analytics={{
        add: "AddDeviceTagClicked",
        remove: "RemoveDeviceTagClicked",
        props: { deviceId: device.device_id },
      }}
    />
  );
};
